import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { 
  PageHeader, 
  Alert, 
  LoadingState, 
  Tabs, 
  Tab, 
  Button, 
  Card 
} from '../components/ui';
import { 
  ArrowPathIcon, 
  StopIcon, 
  TrashIcon,
  ChartBarIcon,
  ClockIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import processService from '../services/processService';
import ProcessCard from '../components/ProcessManager/ProcessCard';
import ProcessStatistics from '../components/ProcessManager/ProcessStatistics';
import ProcessRestorer from '../components/ProcessManager/ProcessRestorer';

const Processes = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [selectedProcessTypes, setSelectedProcessTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [timeframe, setTimeframe] = useState('24h');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [pagination, setPagination] = useState({ offset: 0, limit: 50 });

  const queryClient = useQueryClient();

  // Query for active processes
  const { 
    data: activeProcesses = [], 
    isLoading: isLoadingActive, 
    error: activeError,
    refetch: refetchActive
  } = useQuery(
    ['processes', 'active'],
    () => processService.getActiveProcesses().then(res => res.processes || []),
    {
      refetchInterval: 5000, // Refresh every 5 seconds
      retry: 1,
      onError: (err) => {
        console.error('Error fetching active processes:', err);
        setError('Failed to fetch active processes');
      }
    }
  );

  // Query for all processes
  const { 
    data: allProcessesData, 
    isLoading: isLoadingAll, 
    error: allError,
    refetch: refetchAll
  } = useQuery(
    ['processes', 'all', selectedProcessTypes, selectedStatuses, pagination],
    () => processService.getAllProcesses({
      process_type: selectedProcessTypes.length > 0 ? selectedProcessTypes.join(',') : undefined,
      status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
      ...pagination
    }),
    {
      enabled: activeTab === 'all',
      retry: 1,
      keepPreviousData: true,
      onError: (err) => {
        console.error('Error fetching all processes:', err);
        setError('Failed to fetch processes');
      }
    }
  );

  // Query for statistics
  const { 
    data: statistics, 
    isLoading: isLoadingStats,
    refetch: refetchStats
  } = useQuery(
    ['processes', 'statistics', timeframe],
    () => processService.getProcessStatistics(timeframe).then(res => res.statistics),
    {
      enabled: activeTab === 'statistics',
      retry: 1,
      refetchInterval: 30000, // Refresh every 30 seconds
      onError: (err) => {
        console.error('Error fetching statistics:', err);
        setError('Failed to fetch statistics');
      }
    }
  );

  const allProcesses = allProcessesData?.processes || [];
  const hasMore = allProcessesData?.pagination?.hasMore || false;

  // Clear errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle process cancellation
  const handleCancelProcess = async (processId) => {
    try {
      await processService.cancelProcess(processId);
      setSuccess('Process cancelled successfully');
      queryClient.invalidateQueries(['processes']);
    } catch (err) {
      console.error('Error cancelling process:', err);
      setError(`Failed to cancel process: ${err.message}`);
    }
  };

  // Handle cleanup
  const handleCleanup = async () => {
    try {
      setIsCleaningUp(true);
      setError(null);
      
      const result = await processService.cleanupProcesses({
        max_age_hours: 24,
        mark_stuck_as_failed: true,
        stuck_threshold_minutes: 30
      });
      
      setSuccess(`Cleanup completed: ${result.results.cleaned_old} old processes removed, ${result.results.marked_failed} stuck processes marked as failed`);
      setShowCleanupModal(false);
      queryClient.invalidateQueries(['processes']);
      
    } catch (err) {
      console.error('Error during cleanup:', err);
      setError(`Cleanup failed: ${err.message}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    queryClient.invalidateQueries(['processes']);
    refetchActive();
    refetchAll();
    refetchStats();
  };

  // Handle pagination
  const handleLoadMore = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  // Process type options
  const processTypeOptions = [
    { value: 'BASIC_INFO', label: 'Basic Info' },
    { value: 'GROUP_JOIN', label: 'Group Join' },
    { value: 'AI_ANALYSIS', label: 'AI Analysis' },
    { value: 'ACCOUNT_SCAN', label: 'Account Scan' },
    { value: 'UNSUBSCRIBE', label: 'Unsubscribe' },
    { value: 'ADD_GROUPS', label: 'Add Groups' }
  ];

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'paused', label: 'Paused' }
  ];

  return (
    <div>
      <PageHeader
        title="Process Management"
        description="Monitor and manage all system processes"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              icon={ArrowPathIcon}
              onClick={handleRefresh}
              disabled={isLoadingActive || isLoadingAll}
            >
              Refresh
            </Button>
            <Button
              variant="danger"
              icon={TrashIcon}
              onClick={() => setShowCleanupModal(true)}
              disabled={isCleaningUp}
            >
              {isCleaningUp ? 'Cleaning...' : 'Cleanup'}
            </Button>
          </div>
        }
      />

      {/* Process Recovery Component */}
      <ProcessRestorer />

      {/* Error/Success Messages */}
      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        {/* Active Processes Tab */}
        <Tab id="active" label={`Active (${activeProcesses.length})`}>
          <div className="space-y-4">
            {isLoadingActive ? (
              <LoadingState message="Loading active processes..." />
            ) : activeProcesses.length === 0 ? (
              <Card>
                <div className="p-8 text-center">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Processes</h3>
                  <p className="text-gray-500">All processes are currently completed or inactive.</p>
                </div>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {activeProcesses.map(process => (
                  <ProcessCard
                    key={process.id}
                    process={process}
                    onCancel={handleCancelProcess}
                    showDetails={true}
                  />
                ))}
              </div>
            )}
          </div>
        </Tab>

        {/* All Processes Tab */}
        <Tab id="all" label="All Processes">
          <div className="space-y-4">
            {/* Filters */}
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Process Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Process Types
                    </label>
                    <div className="space-y-2">
                      {processTypeOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedProcessTypes.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProcessTypes([...selectedProcessTypes, option.value]);
                              } else {
                                setSelectedProcessTypes(selectedProcessTypes.filter(t => t !== option.value));
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <div className="space-y-2">
                      {statusOptions.map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedStatuses.includes(option.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStatuses([...selectedStatuses, option.value]);
                              } else {
                                setSelectedStatuses(selectedStatuses.filter(s => s !== option.value));
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedProcessTypes([]);
                      setSelectedStatuses([]);
                      setPagination({ offset: 0, limit: 50 });
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </Card>

            {/* Process List */}
            {isLoadingAll ? (
              <LoadingState message="Loading processes..." />
            ) : allProcesses.length === 0 ? (
              <Card>
                <div className="p-8 text-center">
                  <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Processes Found</h3>
                  <p className="text-gray-500">No processes match your current filters.</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {allProcesses.map(process => (
                    <ProcessCard
                      key={process.id}
                      process={process}
                      onCancel={handleCancelProcess}
                      showDetails={true}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center">
                    <Button
                      variant="secondary"
                      onClick={handleLoadMore}
                      disabled={isLoadingAll}
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Tab>

        {/* Statistics Tab */}
        <Tab id="statistics" label="Statistics">
          <div className="space-y-4">
            {/* Timeframe Selector */}
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Time Frame</h3>
                <div className="flex space-x-2">
                  {['1h', '24h', '7d', '30d'].map(tf => (
                    <Button
                      key={tf}
                      variant={timeframe === tf ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={() => setTimeframe(tf)}
                    >
                      {tf}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Statistics Display */}
            {isLoadingStats ? (
              <LoadingState message="Loading statistics..." />
            ) : (
              <ProcessStatistics statistics={statistics} timeframe={timeframe} />
            )}
          </div>
        </Tab>
      </Tabs>

      {/* Cleanup Confirmation Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-8 bg-white w-full max-w-md m-auto rounded-lg shadow-lg">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                Cleanup Processes
              </h3>
              
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  This will remove old completed/failed processes and mark stuck processes as failed.
                </p>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Warning:</strong> This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3 justify-center">
                <Button
                  variant="secondary"
                  onClick={() => setShowCleanupModal(false)}
                  disabled={isCleaningUp}
                >
                  Cancel
                </Button>
                
                <Button
                  variant="danger"
                  onClick={handleCleanup}
                  disabled={isCleaningUp}
                >
                  {isCleaningUp ? 'Cleaning...' : 'Cleanup'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Processes;
